import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Loader2, CheckCircle, AlertCircle, X, Settings,
  MessageSquare, Send, ChevronDown, ChevronUp, Sparkles, Info, Zap, Share2, Copy, Check, Fuel
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// ── Helpers ─────────────────────────────────────────────────────────

function adjustColor(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amount));
  const b = Math.max(0, Math.min(255, (num & 0x0000FF) + amount));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}

// Lightweight markdown renderer — handles headings, bold, italic, lists, code, paragraphs
function renderMarkdown(text) {
  if (!text || typeof text !== 'string') return null;

  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Headings
    if (line.startsWith('#### ')) {
      elements.push(<h4 key={i} className="text-sm font-bold mt-4 mb-1">{formatInline(line.slice(5))}</h4>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-base font-bold mt-5 mb-1.5">{formatInline(line.slice(4))}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-lg font-bold mt-6 mb-2">{formatInline(line.slice(3))}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-xl font-bold mt-6 mb-2">{formatInline(line.slice(2))}</h1>);
    }
    // Code blocks
    else if (line.startsWith('```')) {
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={`code-${i}`} className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 text-sm overflow-x-auto my-3 font-mono">
          {codeLines.join('\n')}
        </pre>
      );
    }
    // Unordered list items
    else if (line.match(/^[\-\*]\s/)) {
      const listItems = [];
      while (i < lines.length && lines[i].match(/^[\-\*]\s/)) {
        listItems.push(<li key={i} className="ml-4">{formatInline(lines[i].slice(2))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc pl-4 my-2 space-y-1">{listItems}</ul>);
      continue;
    }
    // Numbered list items
    else if (line.match(/^\d+\.\s/)) {
      const listItems = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
        listItems.push(<li key={i} className="ml-4">{formatInline(lines[i].replace(/^\d+\.\s/, ''))}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal pl-4 my-2 space-y-1">{listItems}</ol>);
      continue;
    }
    // Empty line = paragraph break
    else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    }
    // Regular paragraph
    else {
      elements.push(<p key={i} className="my-1 leading-relaxed">{formatInline(line)}</p>);
    }
    i++;
  }

  return elements;
}

// Inline formatting: **bold**, *italic*, `code`, [link](url)
function formatInline(text) {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Italic
    const italicMatch = remaining.match(/\*(.+?)\*/);
    // Inline code
    const codeMatch = remaining.match(/`(.+?)`/);

    const matches = [boldMatch, italicMatch, codeMatch].filter(Boolean);
    if (matches.length === 0) {
      parts.push(remaining);
      break;
    }

    const earliest = matches.reduce((a, b) => (a.index < b.index ? a : b));

    if (earliest.index > 0) {
      parts.push(remaining.slice(0, earliest.index));
    }

    if (earliest === boldMatch) {
      parts.push(<strong key={key++}>{earliest[1]}</strong>);
    } else if (earliest === codeMatch) {
      parts.push(<code key={key++} className="bg-gray-200 dark:bg-gray-700 px-1 rounded text-sm">{earliest[1]}</code>);
    } else if (earliest === italicMatch) {
      parts.push(<em key={key++}>{earliest[1]}</em>);
    }

    remaining = remaining.slice(earliest.index + earliest[0].length);
  }

  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}


// Fuzzy match a field name to a variable_dict key
// Handles LLM-generated names that may differ from original keys (camelCase vs snake_case, etc.)
function findVarDictMatch(fieldName, variableDict) {
  if (!fieldName || !variableDict) return null;
  // Exact match first
  if (variableDict[fieldName]) return { key: fieldName, value: variableDict[fieldName] };
  // Normalize: lowercase, remove underscores/spaces/hyphens
  const normalize = (s) => s.toLowerCase().replace(/[_\s-]/g, '');
  const normalizedField = normalize(fieldName);
  for (const [key, value] of Object.entries(variableDict)) {
    if (normalize(key) === normalizedField) return { key, value };
  }
  return null;
}


// ── Main Component ──────────────────────────────────────────────────

const PrototypeView = ({ agentId, agentDefinition, isDarkMode, token, executionApi, externalConfig, configUpdate, onConfigChange, autoExecute, onAutoExecuteComplete, onInsufficientCredits, isShareMode, userId }) => {
  const navigate = useNavigate();

  // Proto config (from LLM or fallback)
  const [protoConfig, setProtoConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [formValues, setFormValues] = useState({});

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionStatus, setExecutionStatus] = useState(null);
  const [executionOutput, setExecutionOutput] = useState(null);
  const [nodeResults, setNodeResults] = useState([]);
  const [totalNodes, setTotalNodes] = useState(0);
  const [executionError, setExecutionError] = useState(null);

  // UI state
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState({});

  // White label (synced from protoConfig)
  const [appName, setAppName] = useState('');
  const [description, setDescription] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#0077A8');
  const [logoUrl, setLogoUrl] = useState('');
  const [protoTheme, setProtoTheme] = useState(isDarkMode ? 'dark' : 'light');
  const [submitLabel, setSubmitLabel] = useState('Run');
  const [outputFormat, setOutputFormat] = useState('auto');

  // Help chat state
  const [helpChatOpen, setHelpChatOpen] = useState(false);
  const [helpMessages, setHelpMessages] = useState([]);
  const [helpInput, setHelpInput] = useState('');
  const [isHelpLoading, setIsHelpLoading] = useState(false);

  const [needsCredits, setNeedsCredits] = useState(false);
  const [shareTooltip, setShareTooltip] = useState(null); // 'copied' | null

  const abortControllerRef = useRef(null);
  const helpEndRef = useRef(null);
  const helpInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const autoExecuteFired = useRef(false);
  const saveConfigTimer = useRef(null);

  // Derive API URLs
  const protoApi = executionApi ? executionApi.replace('/v1/execution', '/v1/prototype') : '';

  // Theme
  const dark = protoTheme === 'dark';
  const hoverColor = adjustColor(primaryColor, -20);

  // ── Load config on mount: use cached parent config OR fetch from LLM ──

  const configInitialized = useRef(false);

  useEffect(() => {
    if (configInitialized.current) return;

    // If parent has a cached config (e.g. share mode), reuse it — no agentDefinition needed
    if (externalConfig) {
      applyConfig(externalConfig);
      configInitialized.current = true;
      return;
    }

    // For fresh config generation, agentDefinition is required
    if (!agentDefinition) return;

    // Otherwise fetch from LLM
    setConfigLoading(true);

    fetch(`${protoApi}/generate-config?user_id=${userId}&agent_id=${agentId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
      body: JSON.stringify({ agentDefinition }),
    })
      .then(res => {
        if (res.status === 402) { onInsufficientCredits?.(); setConfigLoading(false); applyFallbackConfig(); configInitialized.current = true; return null; }
        return res.json();
      })
      .then(config => {
        if (!config) return;
        applyConfig(config);
        configInitialized.current = true;
        setConfigLoading(false);
      })
      .catch(err => {
        console.error('Config generation failed, using fallback:', err);
        applyFallbackConfig();
        configInitialized.current = true;
        setConfigLoading(false);
      });
  }, [agentDefinition]);

  // Apply chat-driven config updates (from orchestrator prototype_edit)
  // Inline the application to avoid stale closure issues with applyConfig
  useEffect(() => {
    if (!configUpdate) return;
    const config = configUpdate.config;
    if (!config) return;

    setAppName(config.appName || 'AI Agent');
    setDescription(config.description || '');
    setPrimaryColor(config.primaryColor || '#0077A8');
    setLogoUrl(config.logoUrl || '');
    if (config.theme) setProtoTheme(config.theme);
    setSubmitLabel(config.submitLabel || 'Run');
    setOutputFormat(config.outputFormat || 'auto');

    const fields = config.inputFields || protoConfig?.inputFields || [];
    setProtoConfig({ ...config, inputFields: fields });
    // Don't reset formValues — preserve user's entered data
  }, [configUpdate]);

  // Notify parent whenever local config changes (for persistence across navigation)
  useEffect(() => {
    if (protoConfig && onConfigChange) {
      onConfigChange(protoConfig);
    }
  }, [protoConfig]);

  // Debounced persist of config changes to backend (2s after last change)
  useEffect(() => {
    if (!protoConfig || !agentId || isShareMode) return;
    clearTimeout(saveConfigTimer.current);
    saveConfigTimer.current = setTimeout(() => {
      fetch(`${protoApi}/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
        body: JSON.stringify({ agentId, config: protoConfig }),
      }).catch(err => console.warn('Failed to persist prototype config:', err));
    }, 2000);
    return () => clearTimeout(saveConfigTimer.current);
  }, [protoConfig, agentId, isShareMode, protoApi, token]);

  // Apply LLM-generated config
  const applyConfig = useCallback((config) => {
    setAppName(config.appName || 'AI Agent');
    setDescription(config.description || '');
    setPrimaryColor(config.primaryColor || '#0077A8');
    setLogoUrl(config.logoUrl || '');
    setProtoTheme(isDarkMode ? 'dark' : 'light');
    setSubmitLabel(config.submitLabel || 'Run');
    setOutputFormat(config.outputFormat || 'auto');

    // Build input fields and initial values from config
    const fields = config.inputFields || [];
    const variableDict = agentDefinition?.variable_dict || {};
    const initialValues = {};

    fields.forEach(field => {
      const match = findVarDictMatch(field.name, variableDict);
      const sampleValue = match?.value?.values?.[0];
      initialValues[field.name] = sampleValue !== undefined && sampleValue !== null
        ? (typeof sampleValue === 'object' ? JSON.stringify(sampleValue, null, 2) : String(sampleValue))
        : (field.defaultValue ?? '');
    });

    setProtoConfig({ ...config, inputFields: fields, theme: isDarkMode ? 'dark' : 'light' });
    setFormValues(initialValues);
  }, [agentDefinition, isDarkMode]);

  // Fallback if LLM fails
  const applyFallbackConfig = useCallback(() => {
    if (!agentDefinition?.variable_dict) return;
    const variableDict = agentDefinition.variable_dict;
    const inputFields = [];
    const initialValues = {};

    Object.entries(variableDict).forEach(([key, varInfo]) => {
      if (varInfo.source === -1 || varInfo.sourceType === 'agentInput') {
        const sampleValue = varInfo.values?.[0];
        const isLong = typeof sampleValue === 'string' && sampleValue.length > 100;
        const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase());

        inputFields.push({
          name: key, label, type: isLong ? 'textarea' : 'text',
          required: true, placeholder: `Enter ${label.toLowerCase()}...`,
          dataType: varInfo.type || 'string'
        });
        initialValues[key] = sampleValue != null
          ? (typeof sampleValue === 'object' ? JSON.stringify(sampleValue, null, 2) : String(sampleValue))
          : '';
      }
    });

    const name = agentDefinition.concept_name || agentDefinition.conceptName || 'AI Agent';
    setProtoConfig({ appName: name, inputFields, submitLabel: 'Run', outputFormat: 'auto' });
    setFormValues(initialValues);
    setAppName(name);
    setSubmitLabel('Run');
  }, [agentDefinition]);

  // Fill form with sample data (uses fuzzy matching for LLM-generated field names)
  const fillSampleData = () => {
    if (!agentDefinition?.variable_dict || !protoConfig) return;
    const variableDict = agentDefinition.variable_dict;
    const samples = {};
    protoConfig.inputFields.forEach(field => {
      const match = findVarDictMatch(field.name, variableDict);
      const sampleValue = match?.value?.values?.[0];
      if (sampleValue != null) {
        samples[field.name] = typeof sampleValue === 'object'
          ? JSON.stringify(sampleValue, null, 2)
          : String(sampleValue);
      }
    });
    setFormValues(prev => ({ ...prev, ...samples }));
  };

  // Logo upload — resize to 128x128 to keep data URL small for share links
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        // Draw centered/cropped square
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, 128, 128);
        const dataUrl = canvas.toDataURL('image/png', 0.8);
        setLogoUrl(dataUrl);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  // Cleanup
  useEffect(() => {
    return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
  }, []);

  // Note: prototype theme is independent — controlled by chat commands or LLM config.
  // No isDarkMode sync here; initial state already defaults from isDarkMode prop.

  // Auto-scroll help chat
  useEffect(() => {
    if (helpEndRef.current) helpEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [helpMessages, isHelpLoading]);

  // ── SSE Execution ─────────────────────────────────────────────────

  const handleSSEEvent = useCallback((event, data) => {
    switch (event) {
      case 'execution_started':
        if (data.totalNodes) setTotalNodes(data.totalNodes);
        break;
      case 'node_started':
        setNodeResults(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = {
            nodeId: data.nodeId, alias: data.alias, nodeType: data.nodeType,
            status: 'running', input: data.input, inputDisplay: data.inputDisplay,
            output: null, display: null, durationMs: null
          };
          return updated;
        });
        break;
      case 'node_completed':
        setNodeResults(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = {
            ...updated[data.nodeIndex], status: 'completed',
            output: data.output, display: data.display,
            durationMs: data.durationMs, tldr: null, tldrLoading: true
          };
          return updated;
        });
        setExecutionOutput({ output: data.output, display: data.display });
        break;
      case 'node_tldr':
        setNodeResults(prev => {
          const updated = [...prev];
          if (updated[data.nodeIndex]) {
            updated[data.nodeIndex] = { ...updated[data.nodeIndex], tldr: data.tldr, tldrLoading: false };
          }
          return updated;
        });
        break;
      case 'node_failed':
        setNodeResults(prev => {
          const updated = [...prev];
          updated[data.nodeIndex] = { ...updated[data.nodeIndex], status: 'failed', error: data.error };
          return updated;
        });
        break;
      case 'execution_completed':
        setExecutionStatus('completed');
        break;
      case 'execution_failed':
        setExecutionStatus('failed');
        setExecutionError(data.error);
        break;
      default: break;
    }
  }, []);

  const handleExecute = useCallback(async () => {
    if (!agentId || isExecuting) return;

    const customInput = {};
    Object.entries(formValues).forEach(([key, value]) => {
      const fieldConfig = protoConfig?.inputFields?.find(f => f.name === key);
      if (fieldConfig?.type === 'number') {
        customInput[key] = Number(value) || 0;
      } else {
        try {
          const parsed = JSON.parse(value);
          customInput[key] = typeof parsed === 'object' ? parsed : value;
        } catch { customInput[key] = value; }
      }
    });

    setIsExecuting(true);
    setExecutionStatus('running');
    setExecutionOutput(null);
    setNodeResults([]);
    setExecutionError(null);
    setNeedsCredits(false);
    setDetailsOpen(false);
    setExpandedNodes({});
    setTotalNodes(0);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${executionApi}/agent/${agentId}/run/stream?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': token } : {}) },
        body: JSON.stringify({ sample_index: 0, custom_input: customInput }),
        signal: controller.signal
      });
      if (response.status === 402) { setNeedsCredits(true); onInsufficientCredits?.(); setIsExecuting(false); setExecutionStatus(null); return; }
      if (!response.ok) throw new Error(`Execution failed: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        let currentEvent = null;
        for (const line of lines) {
          if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
          else if (line.startsWith('data: ') && currentEvent) {
            try { handleSSEEvent(currentEvent, JSON.parse(line.slice(6))); } catch (e) { /* skip */ }
            currentEvent = null;
          } else if (line === '') currentEvent = null;
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      setExecutionStatus('failed');
      setExecutionError(err.message);
    } finally {
      setIsExecuting(false);
      abortControllerRef.current = null;
    }
  }, [agentId, formValues, executionApi, token, protoConfig, isExecuting, handleSSEEvent]);

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsExecuting(false);
      setExecutionStatus('stopped');
    }
  };

  // Auto-execute: fill sample data and run once config is ready (used by auto-pilot)
  useEffect(() => {
    if (!autoExecute || autoExecuteFired.current || !protoConfig || configLoading || isExecuting) return;
    if (!agentDefinition?.variable_dict) return;
    autoExecuteFired.current = true;

    // Fill sample data first
    const variableDict = agentDefinition.variable_dict;
    const samples = {};
    protoConfig.inputFields?.forEach(field => {
      const match = findVarDictMatch(field.name, variableDict);
      const sampleValue = match?.value?.values?.[0];
      if (sampleValue != null) {
        samples[field.name] = typeof sampleValue === 'object'
          ? JSON.stringify(sampleValue, null, 2)
          : String(sampleValue);
      }
    });
    setFormValues(prev => ({ ...prev, ...samples }));

    // Trigger execution after a tick (to let formValues update)
    setTimeout(() => {
      const customInput = {};
      Object.entries(samples).forEach(([key, value]) => {
        const fieldConfig = protoConfig?.inputFields?.find(f => f.name === key);
        if (fieldConfig?.type === 'number') {
          customInput[key] = Number(value) || 0;
        } else {
          try {
            const parsed = JSON.parse(value);
            customInput[key] = typeof parsed === 'object' ? parsed : value;
          } catch { customInput[key] = value; }
        }
      });

      setIsExecuting(true);
      setExecutionStatus('running');
      setExecutionOutput(null);
      setNodeResults([]);
      setExecutionError(null);
      setTotalNodes(0);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      fetch(`${executionApi}/agent/${agentId}/run/stream?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': token } : {}) },
        body: JSON.stringify({ sample_index: 0, custom_input: customInput }),
        signal: controller.signal
      })
        .then(async (response) => {
          if (response.status === 402) { setNeedsCredits(true); onInsufficientCredits?.(); setIsExecuting(false); setExecutionStatus(null); return; }
          if (!response.ok) throw new Error(`Execution failed: ${response.status}`);
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            let currentEvent = null;
            for (const line of lines) {
              if (line.startsWith('event: ')) currentEvent = line.slice(7).trim();
              else if (line.startsWith('data: ') && currentEvent) {
                try { handleSSEEvent(currentEvent, JSON.parse(line.slice(6))); } catch (e) { /* skip */ }
                currentEvent = null;
              } else if (line === '') currentEvent = null;
            }
          }
        })
        .catch(err => {
          if (err.name === 'AbortError') return;
          setExecutionStatus('failed');
          setExecutionError(err.message);
        })
        .finally(() => {
          setIsExecuting(false);
          abortControllerRef.current = null;
          if (onAutoExecuteComplete) onAutoExecuteComplete();
        });
    }, 100);
  }, [autoExecute, protoConfig, configLoading, agentDefinition, agentId, executionApi, token, handleSSEEvent, onAutoExecuteComplete, isExecuting]);

  // ── Help Chat ─────────────────────────────────────────────────────

  const handleHelpSubmit = useCallback(async () => {
    const q = helpInput.trim();
    if (!q || isHelpLoading) return;

    setHelpMessages(prev => [...prev, { role: 'user', content: q }]);
    setHelpInput('');
    setIsHelpLoading(true);

    try {
      const history = helpMessages.slice(-10).map(m => ({ role: m.role, content: m.content }));
      const res = await fetch(`${protoApi}/ask?user_id=${userId}&agent_id=${agentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
        body: JSON.stringify({
          question: q,
          agentDefinition: agentDefinition,
          appName: appName,
          history,
        }),
      });
      if (res.status === 402) { onInsufficientCredits?.(); return; }
      const data = await res.json();
      setHelpMessages(prev => [...prev, { role: 'assistant', content: data.reply || "I'm not sure about that." }]);
    } catch {
      setHelpMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I couldn't process that. Try again." }]);
    } finally {
      setIsHelpLoading(false);
    }
  }, [helpInput, isHelpLoading, helpMessages, protoApi, agentDefinition, appName]);

  // ── Render Output ─────────────────────────────────────────────────

  // ── Smart output rendering helpers ────────────────────────────────

  // Recursively parse any stringified JSON within a parsed structure (defensive frontend fallback)
  const deepParse = (obj) => {
    if (typeof obj === 'string') {
      const trimmed = obj.trim();
      if (trimmed && (trimmed[0] === '{' || trimmed[0] === '[')) {
        try { return deepParse(JSON.parse(trimmed)); } catch { /* not JSON */ }
      }
      return obj;
    }
    if (Array.isArray(obj)) return obj.map(deepParse);
    if (obj && typeof obj === 'object') {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, deepParse(v)]));
    }
    return obj;
  };

  // Humanize a camelCase/snake_case key into a label
  const humanizeKey = (key) => key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, s => s.toUpperCase());

  // Check if value is an array of plain objects (card-renderable)
  const isArrayOfObjects = (v) => Array.isArray(v) && v.length > 0 &&
    v.every(item => item && typeof item === 'object' && !Array.isArray(item));

  // Unwrap [{text: {...}, index: N}] streaming artifacts → extract inner objects
  const unwrapTextIndexPattern = (arr) => {
    const keys = Object.keys(arr[0]);
    const isTextIndex = keys.length <= 3 && keys.includes('text') &&
      arr.every(item => 'text' in item && typeof item.text === 'object' && item.text !== null);
    return isTextIndex ? arr.map(item => item.text) : arr;
  };

  // Render a single scalar/short value inline
  const renderInlineValue = (v) => {
    if (v === null || v === undefined) return <span className={dark ? 'text-gray-500' : 'text-gray-400'}>—</span>;
    if (typeof v === 'boolean') return <span className={v ? 'text-green-500' : 'text-red-400'}>{String(v)}</span>;
    if (typeof v === 'number') return <span className={dark ? 'text-cyan-400' : 'text-cyan-700'}>{v}</span>;
    const str = String(v);
    if (str.length > 300) return renderMarkdown(str);
    return str;
  };

  // Render an object as key-value rows inside a card
  const renderObjectCard = (obj, idx) => (
    <div key={idx} className={`p-4 rounded-xl border ${dark ? 'bg-gray-800/60 border-gray-700/60' : 'bg-white border-gray-200'} space-y-2`}>
      {Object.entries(obj).map(([k, v]) => {
        // Skip internal metadata
        if (k === 'index' || k === 'finish_reason' || k === 'logprobs') return null;
        const isNested = v && typeof v === 'object';
        return (
          <div key={k}>
            <div className={`text-xs font-semibold mb-0.5 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{humanizeKey(k)}</div>
            <div className={`text-sm ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
              {isNested ? renderSmartValue(v) : renderInlineValue(v)}
            </div>
          </div>
        );
      })}
    </div>
  );

  // Render any value smartly — the universal renderer
  const renderSmartValue = (value) => {
    const v = deepParse(value);

    // Array of objects → auto-table if uniform keys, otherwise cards
    if (isArrayOfObjects(v)) {
      const items = unwrapTextIndexPattern(v);
      // Auto-detect table: all items share the same keys
      const keySets = items.map(item => Object.keys(item).sort().join(','));
      if (items.length >= 2 && keySets.every(ks => ks === keySets[0])) {
        const cols = Object.keys(items[0]).map(k => ({ key: k, label: humanizeKey(k) }));
        return renderSmartValue({ columns: cols, rows: items });
      }
      return <div className="space-y-3">{items.map((item, i) => renderObjectCard(item, i))}</div>;
    }

    // Plain array of scalars → comma-separated or bulleted
    if (Array.isArray(v)) {
      if (v.length <= 5 && v.every(x => typeof x !== 'object')) {
        return <span className={`text-sm ${dark ? 'text-gray-200' : 'text-gray-800'}`}>{v.join(', ')}</span>;
      }
      return <pre className={`text-xs overflow-auto whitespace-pre-wrap max-h-96 ${dark ? 'text-gray-300 bg-gray-800/50' : 'text-gray-700 bg-gray-100'} rounded-lg p-3`}>{JSON.stringify(v, null, 2)}</pre>;
    }

    // Table pattern: {columns: [{key, label}, ...], rows: [{...}, ...]}
    if (v && typeof v === 'object' && !Array.isArray(v) &&
        Array.isArray(v.columns) && Array.isArray(v.rows) &&
        v.columns.length > 0 && v.columns[0]?.key) {
      const cols = v.columns;
      return (
        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: dark ? 'rgba(55,65,81,0.6)' : 'rgba(229,231,235,1)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr className={dark ? 'bg-gray-800/80' : 'bg-gray-50'}>
                {cols.map(col => (
                  <th key={col.key} className={`px-4 py-2.5 text-left text-xs font-semibold ${dark ? 'text-gray-300 border-gray-700' : 'text-gray-600 border-gray-200'} border-b`}>
                    {col.label || humanizeKey(col.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {v.rows.map((row, ri) => (
                <tr key={ri} className={`${dark ? 'border-gray-700/50' : 'border-gray-100'} ${ri < v.rows.length - 1 ? 'border-b' : ''}`}>
                  {cols.map(col => {
                    const cell = row[col.key];
                    const cellStr = Array.isArray(cell) ? cell.join(', ')
                      : (cell && typeof cell === 'object') ? JSON.stringify(cell)
                      : String(cell ?? '—');
                    return (
                      <td key={col.key} className={`px-4 py-3 align-top ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
                        {cellStr.length > 200 ? renderMarkdown(cellStr) : cellStr}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Object → single card
    if (v && typeof v === 'object') {
      if (v.full && typeof v.full === 'string' && !/^\s*[\[{]/.test(v.full.trim())) {
        return renderMarkdown(v.full);
      }
      return renderObjectCard(v, 0);
    }

    // String
    const str = String(v ?? '—');
    if (str.length > 200) return renderMarkdown(str);
    return str;
  };

  // ── Main output renderer ───────────────────────────────────────────

  // Sanitize error messages — never leak internal API details to users
  const getUserFriendlyError = (error) => {
    if (!error) return 'Something unexpected happened. Please try again.';
    const e = error.toLowerCase();
    if (e.includes('429') || e.includes('quota') || e.includes('rate limit'))
      return 'Our servers are currently at capacity. Please try again in a few minutes.';
    if (e.includes('401') || e.includes('unauthorized') || e.includes('authentication'))
      return 'Your session has expired. Please refresh the page and try again.';
    if (e.includes('500') || e.includes('internal server'))
      return 'Something went wrong on our end. Please try again shortly.';
    if (e.includes('timeout') || e.includes('timed out'))
      return 'The request took too long. Please try again.';
    if (e.includes('network') || e.includes('failed to fetch'))
      return 'Network error. Please check your connection and try again.';
    return 'Something unexpected happened. Please try again.';
  };

  const renderOutput = () => {
    if (executionStatus === 'failed') {
      return (
        <div className={`p-4 rounded-lg ${dark ? 'bg-red-900/30 border border-red-700/50' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <AlertCircle className={`w-5 h-5 ${dark ? 'text-red-400' : 'text-red-500'}`} />
            <span className={`font-medium ${dark ? 'text-red-300' : 'text-red-700'}`}>Something went wrong</span>
          </div>
          <p className={`text-sm ${dark ? 'text-red-200/70' : 'text-red-600'}`}>{getUserFriendlyError(executionError)}</p>
        </div>
      );
    }

    if (isExecuting) {
      const completedCount = nodeResults.filter(n => n?.status === 'completed').length;
      return (
        <div className="space-y-3 py-6">
          <div className="flex items-center justify-center space-x-3">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: primaryColor }} />
            <span className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>
              {submitLabel.replace(/^(Generate|Create|Build|Analyze|Score|Process|Run)/i, (match) => {
                const m = match.toLowerCase();
                if (m === 'run') return match[0].toUpperCase() + 'unning';
                return match.replace(/e$/i, '') + 'ing';
              })}...
            </span>
          </div>
          {totalNodes > 0 && (
            <div className="max-w-md mx-auto space-y-1.5">
              <div className={`w-full h-2 rounded-full ${dark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${(completedCount / totalNodes) * 100}%`, backgroundColor: primaryColor }}
                />
              </div>
              <p className={`text-xs text-center ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                Step {completedCount} of {totalNodes}
              </p>
            </div>
          )}
        </div>
      );
    }

    // Display final output
    if (executionOutput?.display?.output?.fields) {
      return (
        <div className={`prose max-w-none ${dark ? 'prose-invert' : ''}`}>
          {executionOutput.display.output.fields.map(field => renderOutputField(field))}
        </div>
      );
    }

    if (executionOutput?.output) {
      // Deep-parse once so all downstream rendering is clean
      const output = deepParse(executionOutput.output);

      // Table pattern at top level: {columns: [{key, label}, ...], rows: [{...}, ...]}
      if (Array.isArray(output.columns) && Array.isArray(output.rows) &&
          output.columns.length > 0 && output.columns[0]?.key) {
        return renderSmartValue(output);
      }

      const looksLikeMarkdown = (v) => typeof v === 'string' &&
        !/^\s*[\[{]/.test(v.trim()) &&
        (/^#{1,4}\s/m.test(v) || v.includes('**') || v.includes('- ') || v.length > 200);

      const outputValues = Object.values(output);
      const hasMarkdown = outputValues.some(looksLikeMarkdown);

      if (hasMarkdown || outputFormat === 'markdown') {
        const mainContent = outputValues.find(v => typeof v === 'string' && v.length > 100) || outputValues.join('\n\n');
        return (
          <div className={`${dark ? 'text-gray-200' : 'text-gray-800'}`}>
            {renderMarkdown(typeof mainContent === 'string' ? mainContent : JSON.stringify(mainContent, null, 2))}
          </div>
        );
      }

      // If output has a single key whose value is an array/object, render it directly
      // (avoids redundant wrapper label like "Generated Personas" > card > card)
      const entries = Object.entries(output);
      if (entries.length === 1) {
        const [key, value] = entries[0];
        return (
          <div>
            <div className={`text-xs font-semibold mb-2 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              {humanizeKey(key)}
            </div>
            {renderSmartValue(value)}
          </div>
        );
      }

      // Multi-key structured output
      return (
        <div className="space-y-4">
          {entries.map(([key, value]) => (
            <div key={key}>
              <div className={`text-xs font-semibold mb-2 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                {humanizeKey(key)}
              </div>
              <div className={`text-sm ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
                {renderSmartValue(value)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  // Render a display field from execution display parser
  const renderOutputField = (field) => {
    if (!field) return null;
    const value = field.display || field.raw;

    return (
      <div key={field.key} className={`py-2 ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
        <div className={field.type === 'longtext' ? '' : `p-3 rounded-lg ${dark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
          <div className={`text-xs font-medium mb-1.5 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{field.label}</div>
          <div className="text-sm">{renderSmartValue(value)}</div>
        </div>
      </div>
    );
  };

  // ── Loading state ─────────────────────────────────────────────────

  if (configLoading) {
    return (
      <div className={`flex flex-col items-center justify-center py-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: '#0077A8' }} />
        <p className="text-sm font-medium">Designing your prototype...</p>
        <p className="text-xs mt-1 opacity-60">Generating a product-quality UI from your agent definition</p>
      </div>
    );
  }

  if (!protoConfig) {
    return (
      <div className={`flex items-center justify-center py-20 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <div className="text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-lg font-medium mb-1">No Agent Data Available</p>
          <p className="text-sm">Complete the workflow pipeline to generate a prototype.</p>
        </div>
      </div>
    );
  }

  // ── Main Render ───────────────────────────────────────────────────

  return (
    <div className={`-m-6 p-6 min-h-full transition-colors duration-300 ${dark ? 'bg-gray-900' : ''}`}>
    <div className={`max-w-2xl mx-auto space-y-6 pb-24 ${dark ? 'text-gray-200' : 'text-gray-800'}`}>

      {/* ─── Header ─── */}
      <div className="text-center pt-4 relative">
        <div className="flex items-center justify-center space-x-3 mb-2">
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          <div className="relative group cursor-pointer" onClick={() => logoInputRef.current?.click()}>
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
            ) : (
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: primaryColor }}
              >
                {appName.charAt(0)}
              </div>
            )}
            <div className={`absolute inset-0 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity ${dark ? 'bg-black/50' : 'bg-black/30'}`}>
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            {logoUrl && (
              <button
                onClick={(e) => { e.stopPropagation(); setLogoUrl(''); }}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-[10px] leading-none hover:bg-red-600"
              >
                ×
              </button>
            )}
          </div>
          <h1 className={`text-2xl font-bold ${dark ? 'text-gray-100' : 'text-gray-900'}`}>
            {appName}
          </h1>
        </div>
        {description && (
          <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{description}</p>
        )}

        {/* Share button */}
        <button
          onClick={async () => {
            try {
              const snapshot = {
                appName,
                description,
                primaryColor,
                logoUrl,
                theme: protoTheme,
                submitLabel,
                outputFormat,
                inputFields: (protoConfig?.inputFields || []).map(f => ({
                  ...f,
                  defaultValue: formValues[f.name] ?? '',
                })),
              };
              const res = await fetch(`${protoApi}/share`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: token } : {}) },
                body: JSON.stringify({ agentId: agentId || 'unknown', config: snapshot }),
              });
              const { shareId } = await res.json();
              const url = new URL(window.location.origin);
              url.searchParams.set('mode', 'share');
              url.searchParams.set('sid', shareId);
              await navigator.clipboard.writeText(url.toString());
              setShareTooltip('copied');
              setTimeout(() => setShareTooltip(null), 2000);
            } catch (e) {
              console.error('Share failed:', e);
            }
          }}
          className={`absolute top-4 right-0 p-2 rounded-lg transition-colors relative ${
            dark
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          }`}
        >
          {shareTooltip === 'copied' ? <Check className="w-4 h-4 text-green-500" /> : <Share2 className="w-4 h-4" />}
          {shareTooltip && (
            <span className="absolute -bottom-7 left-1/2 -translate-x-1/2 text-xs bg-gray-800 text-white px-2 py-1 rounded whitespace-nowrap">
              Link copied!
            </span>
          )}
        </button>
      </div>

      {/* ─── Share Mode: Build Your Own banner ─── */}
      {isShareMode && (
        <div className={`rounded-xl px-5 py-3 border flex items-center justify-between ${dark ? 'bg-gray-800/40 border-gray-700/60' : 'bg-blue-50/60 border-blue-100'}`}>
          <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
            Like what you see? Build your own AI agent in minutes.
          </p>
          <button
            onClick={() => {
              navigate('/');
            }}
            className={`ml-4 shrink-0 px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${dark
              ? 'text-cyan-300 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/20'
              : 'text-blue-700 bg-blue-100 hover:bg-blue-200 border border-blue-200'
            }`}
          >
            Go to Studio
          </button>
        </div>
      )}

      {/* ─── Input Form ─── */}
      <div className={`rounded-xl p-6 border ${dark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className="space-y-4">
          {protoConfig.inputFields.map(field => (
            <div key={field.name}>
              <label className={`block text-sm font-medium mb-1.5 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
                {field.label}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  value={formValues[field.name] || ''}
                  onChange={(e) => setFormValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={field.placeholder}
                  rows={4}
                  className={`w-full px-4 py-3 rounded-lg text-sm border transition-colors focus:outline-none focus:ring-2 ${
                    dark
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500 focus:ring-blue-500/50'
                      : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:ring-blue-500/30'
                  }`}
                />
              ) : (
                <input
                  type={field.type}
                  value={formValues[field.name] || ''}
                  onChange={(e) => setFormValues(prev => ({ ...prev, [field.name]: e.target.value }))}
                  placeholder={field.placeholder}
                  className={`w-full px-4 py-3 rounded-lg text-sm border transition-colors focus:outline-none focus:ring-2 ${
                    dark
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500 focus:ring-blue-500/50'
                      : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:ring-blue-500/30'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Sample data link */}
        {agentDefinition?.variable_dict && (
          <button
            onClick={fillSampleData}
            className={`mt-3 text-xs flex items-center space-x-1 ${dark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
          >
            <Zap className="w-3 h-3" />
            <span>Try with sample data</span>
          </button>
        )}

        {/* Submit / Stop */}
        <div className="mt-5">
          {isExecuting ? (
            <button
              onClick={handleStop}
              className="w-full px-5 py-3 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white flex items-center justify-center space-x-2 transition-colors"
            >
              <X className="w-4 h-4" />
              <span>Stop</span>
            </button>
          ) : (
            <button
              onClick={handleExecute}
              disabled={!agentId}
              className="w-full px-5 py-3 rounded-lg font-semibold text-white flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => { if (agentId) e.currentTarget.style.backgroundColor = hoverColor; }}
              onMouseLeave={(e) => { if (agentId) e.currentTarget.style.backgroundColor = primaryColor; }}
            >
              <Sparkles className="w-4 h-4" />
              <span>{submitLabel}</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── Out of Credits Banner ─── */}
      {needsCredits && (
        <div className={`rounded-xl p-5 border ${dark ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${dark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
              <Fuel className={`w-5 h-5 ${dark ? 'text-amber-400' : 'text-amber-600'}`} />
            </div>
            <div className="flex-1">
              <h4 className={`font-semibold text-sm ${dark ? 'text-amber-300' : 'text-amber-800'}`}>
                Out of Credits
              </h4>
              <p className={`text-sm mt-1 ${dark ? 'text-amber-200/70' : 'text-amber-700'}`}>
                You've used all your available credits. Add more to continue running this prototype.
              </p>
              <div className="flex items-center gap-3 mt-3">
                <button
                  onClick={() => {
                    localStorage.setItem('bytesized_billing_return', window.location.pathname + window.location.search);
                    navigate('/billing?recharge=true');
                  }}
                  className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: primaryColor }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverColor}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = primaryColor}
                >
                  Add Credits
                </button>
                {isShareMode && (
                  <button
                    onClick={() => navigate('/')}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${dark
                      ? 'text-gray-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    Build Your Own
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Output Area ─── */}
      {(isExecuting || executionOutput || executionStatus === 'failed') && (
        <div className={`rounded-xl p-6 border ${dark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          {renderOutput()}
        </div>
      )}

      {/* ─── Processing Details (collapsed) ─── */}
      {nodeResults.length > 0 && !isExecuting && (
        <div className="text-center">
          <button
            onClick={() => setDetailsOpen(!detailsOpen)}
            className={`inline-flex items-center space-x-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${
              dark ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Info className="w-3.5 h-3.5" />
            <span>View processing details</span>
            {detailsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {detailsOpen && (
            <div className={`mt-3 rounded-xl border text-left ${dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`divide-y ${dark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {nodeResults.map((node, idx) => {
                  if (!node) return null;
                  const isExpanded = expandedNodes[idx];
                  return (
                    <div key={idx}>
                      <button
                        onClick={() => setExpandedNodes(prev => ({ ...prev, [idx]: !prev[idx] }))}
                        className={`w-full px-4 py-3 flex items-center justify-between text-left text-sm ${dark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
                      >
                        <div className="flex items-center space-x-2">
                          {node.status === 'completed' && <CheckCircle className="w-3.5 h-3.5 text-green-500" />}
                          {node.status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />}
                          {node.status === 'failed' && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                          <span className={dark ? 'text-gray-300' : 'text-gray-700'}>{node.alias}</span>
                          {node.durationMs && (
                            <span className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                              {(node.durationMs / 1000).toFixed(1)}s
                            </span>
                          )}
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {isExpanded && (
                        <div className={`px-4 pb-4 text-xs space-y-2 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {node.tldr && <p className="italic">{node.tldr}</p>}
                          {node.error && <p className="text-red-500">{node.error}</p>}
                          {node.output && (
                            <pre className={`p-3 rounded-lg overflow-auto max-h-48 ${dark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                              {JSON.stringify(node.output, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Help Chat Bubble ─── */}
      {helpChatOpen ? (
        <div className={`fixed bottom-4 right-4 w-80 rounded-xl border shadow-2xl flex flex-col z-50 ${dark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ height: '400px' }}>
          {/* Header */}
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: dark ? '#374151' : '#E5E7EB' }}>
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-4 h-4" style={{ color: primaryColor }} />
              <span className={`font-medium text-sm ${dark ? 'text-gray-200' : 'text-gray-800'}`}>
                Ask about {appName}
              </span>
            </div>
            <button onClick={() => setHelpChatOpen(false)} className={`p-1 rounded ${dark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {helpMessages.length === 0 && (
              <div className={`text-center py-6 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Ask anything about how {appName} works</p>
              </div>
            )}
            {helpMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'text-white'
                    : dark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                }`} style={msg.role === 'user' ? { backgroundColor: primaryColor } : undefined}>
                  {msg.content}
                </div>
              </div>
            ))}
            {isHelpLoading && (
              <div className="flex justify-start">
                <div className={`rounded-lg px-3 py-2 ${dark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: primaryColor }} />
                </div>
              </div>
            )}
            <div ref={helpEndRef} />
          </div>

          {/* Input */}
          <div className={`px-3 py-3 border-t ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex items-center space-x-2">
              <input
                ref={helpInputRef}
                type="text"
                value={helpInput}
                onChange={(e) => setHelpInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleHelpSubmit(); } }}
                placeholder="Ask a question..."
                className={`flex-1 px-3 py-2 rounded-lg text-sm border focus:outline-none focus:ring-1 ${
                  dark ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400'
                }`}
              />
              <button
                onClick={handleHelpSubmit}
                disabled={!helpInput.trim() || isHelpLoading}
                className="p-2 rounded-lg text-white disabled:opacity-40"
                style={{ backgroundColor: primaryColor }}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => { setHelpChatOpen(true); setTimeout(() => helpInputRef.current?.focus(), 100); }}
          className="fixed bottom-4 right-4 p-3 rounded-full text-white shadow-lg hover:scale-105 transition-transform z-50 flex items-center space-x-2"
          style={{ backgroundColor: primaryColor }}
        >
          <MessageSquare className="w-5 h-5" />
          <span className="text-sm font-medium pr-1">Help</span>
        </button>
      )}
    </div>
    </div>
  );
};

export default PrototypeView;
